import React, { useState } from 'react';
import { Clock } from 'lucide-react';
import { ProjectService } from '../lib/projectServices';
import RichTextDescription from './RichTextDescription';
import { formatCurrency } from '../lib/utils';
import { supabase } from '../lib/supabase';
import EditServiceModal from './booking/EditServiceModal';

interface ProjectServiceItemProps {
  service: ProjectService;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  canEdit: boolean;
  displayProject: { id: string } | null;
  onDelete: (service: ProjectService) => void;
}

export default function ProjectServiceItem({
  service,
  onUpdate,
  onError,
  canEdit,
  displayProject,
  onDelete
}: ProjectServiceItemProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<ProjectService | null>(null);

  const handleEditService = async (updatedService: any) => {
    try {
      if (!displayProject?.id) {
        throw new Error('No project ID');
      }

      // Update the project service
      const { error } = await supabase
        .from('project_services')
        .update({
          name: updatedService.name.trim(),
          description: updatedService.description?.trim() || null,
          price: updatedService.price,
          duration_minutes: updatedService.duration_minutes,
          tax_exempt: updatedService.tax_exempt,
          tax_rate: updatedService.tax_exempt ? 0 : (updatedService.tax_rate || 5),
          tax_amount: updatedService.tax_exempt ? 0 : Math.round((updatedService.price * (updatedService.tax_rate || 5) / 100) * 100) / 100,
          total_amount: Math.round((updatedService.price + (updatedService.tax_exempt ? 0 : Math.round((updatedService.price * (updatedService.tax_rate || 5) / 100) * 100) / 100)) * 100) / 100,
          updated_at: new Date().toISOString()
        })
        .eq('id', service.id);

      if (error) throw error;

      await onUpdate();
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Error updating service:', err);
      onError(err instanceof Error ? err.message : 'Failed to update service');
    }
  };

  return (
    <>
      <div className="px-3 py-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-gray-900 mb-1" style={{ 
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              letterSpacing: '-0.01em'
            }}>
              {service.name}
            </div>
            {service.description && (
              <RichTextDescription description={service.description} className="text-[12px] text-gray-600 mb-1.5 leading-relaxed" />
            )}
            <div className="flex items-center text-[11px] text-gray-500">
              <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
              {(() => {
                if (service.duration_minutes >= 1440) {
                  return `${Math.floor(service.duration_minutes / 1440)} days`;
                } else if (service.duration_minutes >= 60) {
                  const hours = Math.floor(service.duration_minutes / 60);
                  const mins = service.duration_minutes % 60;
                  return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hours`;
                } else {
                  return `${service.duration_minutes} minutes`;
                }
              })()}
            </div>
            {/* Action Buttons - iOS Style */}
            {canEdit && (
              <div className="flex items-center space-x-2 mt-2.5">
                <button
                  onClick={() => {
                    setEditingService(service);
                    setIsEditModalOpen(true);
                  }}
                  className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-[8px] text-[11px] font-medium hover:bg-blue-100 active:bg-blue-200 active:scale-95 transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(service)}
                  className="px-3 py-1.5 bg-red-50 text-red-600 rounded-[8px] text-[11px] font-medium hover:bg-red-100 active:bg-red-200 active:scale-95 transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
          {/* Price - iOS Style */}
          <div className="text-[15px] font-semibold text-gray-900 ml-3 flex-shrink-0" style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            letterSpacing: '-0.01em'
          }}>
            {formatCurrency(service.price)}
          </div>
        </div>
      </div>

      {isEditModalOpen && service && (
        <EditServiceModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingService(null);
          }}
          service={{
            name: service.name || '',
            description: service.description || '',
            price: service.price || 0,
            duration_minutes: service.duration_minutes || 0,
            is_active: true,
            tax_rate: service.tax_rate || 5,
            tax_exempt: service.tax_exempt || false,
            addons: []
          }}
          onSave={handleEditService}
        />
      )}
    </>
  );
}
